// Transport for the rdrive proxy. Plain JS — no Vue, no store, no axios — so
// this file travelled unchanged into `@rdrive/data-transport` (PLATFORM.md §3.3).
//
// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------
// The proxy scheme is ONE rule, defined here and nowhere else:
//
//     https://<proxyUrl><path>?url=<apiUrl>&scopedToken=<jwt>[&...params]
//
// `proxyUrl` and `apiUrl` are hosts only — no scheme, no path (the host passes
// `$apiURL` as e.g. `uk.rdrive.io`). `path` starts with `/`. Extra params are
// appended AFTER `url` and `scopedToken`, which is the order the paging loader
// has always produced.
//
//   buildProxyUrl(config, path, params?) -> string
//   createProxyFetcher(configOrGetter)   -> fetcher
//     fetcher(path, init?, params?)      -> Promise<Response>   (plain fetch)
//     fetcher.url(path, params?)         -> string              (for log lines)
//     fetcher.config()                   -> ProxyConfig         (resolved now)
//
// **Token-refresh seam.** `createProxyFetcher` accepts either a plain config
// object or a GETTER returning one, and calls it on *every* request. Pass a
// getter and a later `getScopedToken` postMessage round-trip can swap the token
// in place — every existing fetcher picks it up on its next call, with no
// consumer re-created and no re-initialisation. Nothing here refreshes
// anything today; this is only the seam.
//
// Not a proxy call and deliberately out of scope: the DuckDB WASM/worker CDN
// fetch in DuckDBService.initialize().

/**
 * @typedef {Object} ProxyConfig
 * @property {string} proxyUrl    proxy host, no scheme (e.g. 'proxy.rdrive.io')
 * @property {string} apiUrl      target API host, no scheme (e.g. 'uk.rdrive.io')
 * @property {string} scopedToken short-lived JWT minted by the host app
 */

/**
 * @typedef {Object} ProxyFetcher
 * @property {(path: string, init?: RequestInit, params?: Object) => Promise<Response>} (callable)
 * @property {(path: string, params?: Object) => string} url
 * @property {() => ProxyConfig} config
 */

/**
 * The single definition of the proxy URL scheme.
 *
 * @param {ProxyConfig} config
 * @param {string} path              path on the target API, starting with '/'
 * @param {Object} [params]          extra query params, appended last
 * @returns {string}
 */
export const buildProxyUrl = ({ proxyUrl, apiUrl, scopedToken }, path, params) => {
  const url = new URL(`https://${proxyUrl}${path}`);

  url.searchParams.append('url', apiUrl);
  url.searchParams.append('scopedToken', scopedToken);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;
      url.searchParams.append(key, value);
    }
  }

  return url.toString();
};

/**
 * Build a fetcher bound to a proxy config.
 *
 * @param {ProxyConfig | (() => ProxyConfig)} configOrGetter  a getter is read on
 *   every call, so the token can be rotated underneath live consumers
 * @returns {ProxyFetcher}
 */
export const createProxyFetcher = (configOrGetter) => {
  const resolve = () =>
    (typeof configOrGetter === 'function' ? configOrGetter() : configOrGetter) || {};

  const fetcher = (path, init, params) => fetch(buildProxyUrl(resolve(), path, params), init);

  fetcher.url = (path, params) => buildProxyUrl(resolve(), path, params);
  fetcher.config = resolve;

  return fetcher;
};
